/**
 * Prime Artifact Properties Manager Component
 * 
 * Central property definition viewer for prime artifacts.
 * Shows all properties across all states with created/updated mappings.
 * Features:
 * - List all properties defined for the artifact
 * - Show states where properties are created/updated
 * - Show referenced artifact types for UUID/UUID[] properties
 * - Add/edit/delete property definitions
 * - Link properties to global option catalogs
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tooltip,
  Alert,
  Badge,
  Typography,
  message,
  Popconfirm,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  ExclamationCircleOutlined,
  SettingOutlined,
  ReloadOutlined,
  SearchOutlined,
  ClearOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { PrimeArtifactState } from '../../types/primeModel';
import OptionCatalogManager from './OptionCatalogManager';
import CalculationBuilder from './CalculationBuilder';
import { PropertyRelationshipService } from '../../services/api/propertyRelationshipService';
import { PropertyRelationshipGraph } from './PropertyRelationshipGraph';
import { PropertyRelationshipDetails } from './PropertyRelationshipDetails';
import { RelationshipType } from '../../types/propertyRelationships';
import { GlobalPropertiesService } from '../../services/api/globalPropertiesService';
import { GlobalPropertyDetailsModal } from '../GlobalProperties/GlobalPropertyDetailsModal';
import { GlobalPropertySelector } from '../StateTemplates/GlobalPropertySelector';
import { SimplifiedPropertyForm } from './SimplifiedPropertyForm';
import { PrimeArtifactGenerationWizard } from './PrimeArtifactGenerationWizard';
import { PrimeArtifactCompletenessDashboard } from './PrimeArtifactCompletenessDashboard';
import { usePrimeArtifactPropertyTable, type PrimeArtifactPropertyRow } from '../../hooks/usePrimeArtifactPropertyTable';
import { usePrimeArtifactPropertyForm } from '../../hooks/usePrimeArtifactPropertyForm';
import { PrimeArtifactPropertyTable } from './PrimeArtifactPropertyTable';
import type { ColumnsType } from 'antd/es/table';
import styles from './PrimeArtifactPropertiesManager.module.css';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface PropertyStateMapping {
  id: string;
  state_name: string;
  mapping_type: 'created' | 'updated';
  transition_ref?: string;
  notes?: string;
}

interface PropertyArtifactReference {
  id: string;
  referenced_artifact_type_id: string;
  referenced_artifact_type_name?: string;
  artifact_id?: string;  // For transformed references - specific artifact instance
  reference_type?: 'uuid' | 'transformed';
  source_property_key?: string;
  transformation_logic?: any;
  relationship_type?: string;
  notes?: string;
}

interface PrimeArtifactProperty {
  id: string;
  prime_artifact_id: string;
  property_key: string;
  property_type: 'string' | 'number' | 'integer' | 'float' | 'boolean' | 'date' | 'date_range' | 'currency' | 'duration' | 'url' | 'uuid' | 'uuid[]' | 'list';
  description?: string;
  is_required: boolean;
  input_source?: 'user_input' | 'system_generated' | 'referenced_artifact' | 'transformed_reference';
  global_option_catalog_ref?: string;
  global_option_catalog_name?: string;
  default_value?: string;
  validation_rules?: Record<string, any>;
  property_scope?: 'artifact' | 'artifact_item';
  is_child_reference?: boolean;
  input_label?: string;
  input_type?: string;
  state_mappings?: PropertyStateMapping[];
  referenced_artifacts?: PropertyArtifactReference[];
  created_at?: string;
  updated_at?: string;
}

interface OptionValue {
  id?: string;
  value: string;
  display_label?: string;
  description?: string;
  sequence_order?: number;
  is_active?: boolean;
}

interface OptionCatalog {
  id: string;
  name: string;
  description?: string;
  category?: string;
  display_label?: string;
  values?: OptionValue[];
}

interface PrimeArtifactPropertiesManagerProps {
  artifactId: string;
  artifactName: string;
  onPropertiesUpdated?: () => void;
}

export const PrimeArtifactPropertiesManager: React.FC<PrimeArtifactPropertiesManagerProps> = ({
  artifactId,
  artifactName,
  onPropertiesUpdated
}) => {
  // Additional state for modals and features not in hooks
  const [showCatalogManager, setShowCatalogManager] = useState(false);
  const [showArtifactCreateModal, setShowArtifactCreateModal] = useState(false);
  const [showRelationshipGraph, setShowRelationshipGraph] = useState(false);
  const [artifactCreateForm] = Form.useForm();
  const [allPrimeArtifacts, setAllPrimeArtifacts] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingAllArtifacts, setLoadingAllArtifacts] = useState(false);
  const [stateTemplates, setStateTemplates] = useState<Array<{ id: string; name: string; version?: string }>>([]);
  const [loadingStateTemplates, setLoadingStateTemplates] = useState(false);

  // Local state for selected property (needed before hook initialization)
  const [selectedPropertyForDetails, setSelectedPropertyForDetails] = useState<string | null>(null);
  const [applyLoading, setApplyLoading] = useState(false);
  
  // State for simplified property form
  const [showSimplifiedPropertyForm, setShowSimplifiedPropertyForm] = useState(false);
  const [simplifiedFormPropertyKey, setSimplifiedFormPropertyKey] = useState<string>('');
  const [simplifiedFormPropertyId, setSimplifiedFormPropertyId] = useState<string | undefined>(undefined);
  const [simplifiedFormOverrides, setSimplifiedFormOverrides] = useState<any>(undefined);
  
  // State for generation wizard
  const [showGenerationWizard, setShowGenerationWizard] = useState(false);

  // Table hook
  const {
    properties,
    loading,
    searchText,
    setSearchText,
    filterSource,
    setFilterSource,
    filterType,
    setFilterType,
    propertyGraphData,
    tableData,
    filteredProperties,
    fetchProperties,
    handleRefresh,
    handleFormSuccess,
    getPropertyRelationshipType,
    getPropertyDependencyCounts
  } = usePrimeArtifactPropertyTable({
    artifactId,
    onPropertiesUpdated,
    onRowClick: undefined,
    selectedPropertyId: selectedPropertyForDetails
  });

  // Form hook
  const {
    form,
    showModal,
    setShowModal,
    editingProperty,
    setEditingProperty,
    optionCatalogs,
    artifactTypes,
    artifactStates,
    sourceArtifacts,
    setSourceArtifacts,
    sourceArtifactsLoading,
    sourceArtifactProperties,
    setSourceArtifactProperties,
    sourceArtifactPropertiesMap,
    sourcePropertiesLoading,
    selectedSourceArtifactType,
    setSelectedSourceArtifactType,
    missingProperties,
    propertiesUpdateKey,
    showGlobalPropertySelector,
    setShowGlobalPropertySelector,
    showGlobalPropertyModal,
    setShowGlobalPropertyModal,
    selectedGlobalPropertyKey,
    setSelectedGlobalPropertyKey,
    globalPropertyExists,
    setGlobalPropertyExists,
    fetchOptionCatalogs,
    fetchArtifactTypes,
    fetchArtifactStates,
    fetchSourceArtifacts,
    fetchSourceArtifactProperties,
    checkMissingProperties,
    handleAddProperty,
    handlePropertySelect,
    handleEditProperty,
    handleDeleteProperty,
    handleSaveProperty
  } = usePrimeArtifactPropertyForm({
    artifactId,
    artifactName,
    onPropertySaved: () => {
      handleFormSuccess();
      onPropertiesUpdated?.();
    },
    onPropertiesUpdated
  });

  const handleApplyProperties = async () => {
    try {
      setApplyLoading(true);
      const response = await apiClient.post(`/prime-artifacts/${artifactId}/apply-properties`, {
        property_source: 'auto'
      });
      
      message.success(
        `Applied ${response.data.properties_created || 0} properties. ` +
        `${response.data.properties_skipped || 0} properties already existed.`
      );
      
      // Refresh the properties list
      await fetchProperties();
      onPropertiesUpdated?.();
    } catch (error: any) {
      console.error('Error applying properties:', error);
      message.error(`Failed to apply properties: ${error.response?.data?.detail || error.message}`);
    } finally {
      setApplyLoading(false);
    }
  };

  // Handler to open simplified property form for editing
  const handleOpenSimplifiedEdit = (property: PrimeArtifactProperty) => {
    setSimplifiedFormPropertyKey(property.property_key);
    setSimplifiedFormPropertyId(property.id);
    setSimplifiedFormOverrides({
      is_required: property.is_required,
      default_value: property.default_value,
      property_scope: property.property_scope,
      is_child_reference: property.is_child_reference,
      state_mappings: property.state_mappings,
      referenced_artifacts: property.referenced_artifacts
    });
    setShowSimplifiedPropertyForm(true);
  };

  // Handler to close simplified form and refresh
  const handleSimplifiedFormClose = () => {
    setShowSimplifiedPropertyForm(false);
    setSimplifiedFormPropertyKey('');
    setSimplifiedFormPropertyId(undefined);
    setSimplifiedFormOverrides(undefined);
  };

  const handleSimplifiedFormSave = () => {
    handleSimplifiedFormClose();
    fetchProperties();
    onPropertiesUpdated?.();
  };

  // Fetch all prime artifacts for parent selection
  const fetchAllPrimeArtifacts = useCallback(async () => {
    setLoadingAllArtifacts(true);
    try {
      const response = await apiClient.get('/prime-artifacts');
      const artifacts = Array.isArray(response.data) ? response.data : [];
      setAllPrimeArtifacts(artifacts.map((a: any) => ({ id: a.id, name: a.name })));
    } catch (error) {
      console.error('Error fetching all prime artifacts:', error);
      setAllPrimeArtifacts([]);
    } finally {
      setLoadingAllArtifacts(false);
    }
  }, []);

  // Fetch state templates
  const fetchStateTemplates = useCallback(async () => {
    setLoadingStateTemplates(true);
    try {
      const response = await apiClient.get('/state-templates?include_deleted=false');
      const templates = response.data || [];
      setStateTemplates(templates.map((t: any) => ({
        id: t.id,
        name: t.name,
        version: t.version
      })));
    } catch (error) {
      console.error('Error fetching state templates:', error);
      setStateTemplates([]);
    } finally {
      setLoadingStateTemplates(false);
    }
  }, []);


  // Create new artifact
  const handleCreateArtifact = async () => {
    try {
      const values = await artifactCreateForm.validateFields();
      console.log('[PrimeArtifactPropertiesManager] Creating artifact with form values:', values);
      console.log('[PrimeArtifactPropertiesManager] Current artifactId (parent):', artifactId);
      
      const artifactData: any = {
        name: values.name,
        artifact_type_id: values.artifact_type_id,
        description: values.description
      };
      
      // If parent_ref is selected, set it
      if (values.parent_ref) {
        artifactData.parent_ref = values.parent_ref;
        console.log('[PrimeArtifactPropertiesManager] Parent artifact selected - setting parent_ref to:', values.parent_ref);
      }
      
      // If state_template_id is selected, set it
      if (values.state_template_id) {
        artifactData.state_template_id = values.state_template_id;
        console.log('[PrimeArtifactPropertiesManager] State template selected - setting state_template_id to:', values.state_template_id);
      }
      
      console.log('[PrimeArtifactPropertiesManager] Final artifact data being sent:', artifactData);
      
      const response = await apiClient.post('/prime-artifacts', artifactData);
      
      message.success('Artifact created successfully');
      setShowArtifactCreateModal(false);
      artifactCreateForm.resetFields();
      
      // Refresh artifacts list if we're viewing that type
      if (selectedSourceArtifactType) {
        await fetchSourceArtifacts(selectedSourceArtifactType);
      }
      
      return response.data.id;
    } catch (error: any) {
      message.error('Failed to create artifact: ' + (error.response?.data?.detail || error.message));
      throw error;
    }
  };

  // Create property on source artifact
  const handleCreatePropertyOnSourceArtifact = useCallback(async (
    sourceArtifactId: string,
    propertyKey: string,
    fieldName: number
  ) => {
    try {
      // Get current property form values to copy relevant details
      const currentProperty = form.getFieldsValue();
      
      // Create the property on the source artifact
      const propertyData = {
        property_key: propertyKey,
        property_type: currentProperty.property_type || 'string',
        description: currentProperty.description || `Property ${propertyKey} from ${artifactName || 'source artifact'}`,
        is_required: currentProperty.is_required || false,
        input_source: 'user_input', // Default for source artifact properties
        default_value: currentProperty.default_value,
        validation_rules: currentProperty.validation_rules || {},
        // Backend requires at least one artifact reference
        referenced_artifacts: [{
          artifact_id: sourceArtifactId,
          reference_type: 'self',
          referenced_artifact_type_id: null
        }],
        state_mappings: []
      };

      await apiClient.post(`/prime-artifacts/${sourceArtifactId}/properties`, propertyData);
      
      message.success(`Property "${propertyKey}" created on source artifact`);
      
      // Refresh properties for the source artifact
      await fetchSourceArtifactProperties(sourceArtifactId);
      
      // Set the source property key in the form
      // Check which form list this is for (transformed_references or referenced_artifact_types)
      const transformedRefs = form.getFieldValue('transformed_references');
      const referencedTypes = form.getFieldValue('referenced_artifact_types');
      
      if (transformedRefs && transformedRefs[fieldName] !== undefined) {
        form.setFieldValue(['transformed_references', fieldName, 'source_property_key'], propertyKey);
      } else if (referencedTypes && referencedTypes[fieldName] !== undefined) {
        form.setFieldValue(['referenced_artifact_types', fieldName, 'source_property_key'], propertyKey);
        // Update hidden field to trigger re-render when properties are loaded
        form.setFieldValue(['_properties_loaded', fieldName], Date.now());
      }
      
    } catch (error: any) {
      console.error('Error creating property on source artifact:', error);
      message.error('Failed to create property on source artifact: ' + (error.response?.data?.detail || error.message));
    }
  }, [form, artifactName, fetchSourceArtifactProperties]);

  useEffect(() => {
    void fetchProperties();
    void fetchOptionCatalogs();
    void fetchArtifactTypes();
    void fetchArtifactStates();
  }, [fetchProperties, fetchOptionCatalogs, fetchArtifactTypes, fetchArtifactStates]);

  // Fetch all artifacts when create modal opens
  useEffect(() => {
    if (showArtifactCreateModal) {
      fetchAllPrimeArtifacts();
      fetchStateTemplates();
    }
  }, [showArtifactCreateModal, fetchAllPrimeArtifacts, fetchStateTemplates]);

  // Ensure form values are properly set when modal opens with editing property
  useEffect(() => {
    if (showModal && editingProperty) {
      // Small delay to allow Form.List to initialize
      const timer = setTimeout(() => {
        // Check if referenced_artifact_types should have values but Form.List is empty
        const currentRefs = form.getFieldValue('referenced_artifact_types');
        const inputSource = form.getFieldValue('input_source');
        
        console.log('useEffect check - showModal:', showModal, 'inputSource:', inputSource, 'currentRefs:', currentRefs);
        
        if (inputSource === 'referenced_artifact' && (!currentRefs || currentRefs.length === 0)) {
          // Form values might not have been set correctly, try to reload from property
          const property = editingProperty;
          console.log('Form.List appears empty, reloading from property:', property.referenced_artifacts);
          
          if (property.referenced_artifacts && property.referenced_artifacts.length > 0) {
            // Re-extract and set values
            const allRefs = property.referenced_artifacts.map(ref => ({
              artifact_type_id: ref.referenced_artifact_type_id,
              artifact_id: ref.artifact_id,
              source_property_key: ref.source_property_key,
              transformation_logic: ref.transformation_logic,
              relationship_type: ref.relationship_type,
              notes: ref.notes,
              reference_type: ref.reference_type || (!ref.source_property_key && !ref.transformation_logic ? 'uuid' : 'transformed')
            }));
            
            // Use the same logic as handleEditProperty - if input_source is referenced_artifact,
            // treat all references as UUID refs regardless of transformation_logic
            let uuidRefs: typeof allRefs = [];
            if (property.input_source === 'referenced_artifact') {
              uuidRefs = allRefs;
              console.log('useEffect - Property input_source is referenced_artifact - treating all refs as UUID refs');
            } else {
              // For other input_source types, use filtering logic
              uuidRefs = allRefs.filter(ref => {
                let hasTransformationLogic = false;
                if (ref.transformation_logic) {
                  if (typeof ref.transformation_logic === 'object') {
                    const keys = Object.keys(ref.transformation_logic);
                    hasTransformationLogic = keys.length > 0;
                  } else {
                    hasTransformationLogic = true;
                  }
                }
                return !hasTransformationLogic && (ref.reference_type === 'uuid' || !ref.reference_type);
              });
            }
            
            console.log('useEffect - UUID refs filtered:', uuidRefs);
            
            if (uuidRefs.length > 0) {
              // Deduplicate by artifact_id + source_property_key before setting form values
              const seenRefs = new Set<string>();
              const uniqueRefs = uuidRefs.filter(ref => {
                const refKey = `${ref.artifact_id}-${ref.source_property_key || ''}`;
                if (seenRefs.has(refKey)) {
                  console.warn('useEffect - Deduplicating reference:', refKey);
                  return false;
                }
                seenRefs.add(refKey);
                return true;
              });
              
              const formValues = uniqueRefs.map(ref => ({
                artifact_type_id: ref.artifact_type_id ? String(ref.artifact_type_id) : undefined,
                artifact_id: ref.artifact_id ? String(ref.artifact_id) : undefined,
                source_property_key: ref.source_property_key || undefined,
                relationship_type: ref.relationship_type || undefined,
                notes: ref.notes || undefined
              }));
              
              console.log('Re-setting referenced_artifact_types (deduplicated):', formValues, 'from', uuidRefs.length, 'original');
              form.setFieldValue('referenced_artifact_types', formValues);
            } else {
              console.warn('No UUID refs found after filtering, but property has', property.referenced_artifacts?.length, 'references');
            }
          }
        }
      }, 100); // Small delay to allow Form.List to mount
      
      return () => clearTimeout(timer);
    }
  }, [showModal, editingProperty, form]);

  // Note: handleAddProperty, handlePropertySelect, handleEditProperty, handleDeleteProperty, handleSaveProperty
  // are now provided by usePrimeArtifactPropertyForm hook

  const getPropertyTypeTag = (type: string) => {
    const colorMap: Record<string, string> = {
      string: 'blue',
      number: 'green',
      boolean: 'orange',
      date: 'purple',
      currency: 'cyan',
      duration: 'geekblue',
      url: 'gold',
      uuid: 'magenta',
      'uuid[]': 'red',
      list: 'lime'
    };
    return <Tag color={colorMap[type] || 'default'}>{type}</Tag>;
  };

  // Build table columns with handlers from hooks
  const columns: ColumnsType<PrimeArtifactPropertyRow> = [
    {
      title: 'Property Key',
      dataIndex: 'property_key',
      key: 'property_key',
      width: 250,
      sorter: (a: PrimeArtifactPropertyRow, b: PrimeArtifactPropertyRow) => 
        a.property_key.localeCompare(b.property_key),
      render: (text: string, record: PrimeArtifactPropertyRow) => (
        <Space direction="vertical" size={0}>
          <Space>
            <strong>{text}</strong>
            {record.is_required && <Tag color="red">Required</Tag>}
          </Space>
          <Space size="small">
            <Tooltip title="All properties inherit from global property definitions">
              <Tag color="geekblue" style={{ fontSize: 10 }}>
                From Global
              </Tag>
            </Tooltip>
            <Button
              type="link"
              size="small"
              icon={<InfoCircleOutlined />}
              onClick={async () => {
                setSelectedGlobalPropertyKey(text);
                // Check if global property exists
                try {
                  await GlobalPropertiesService.getPropertyDefinition(text);
                  setGlobalPropertyExists(prev => new Map(prev).set(text, true));
                  setShowGlobalPropertyModal(true);
                } catch (error) {
                  setGlobalPropertyExists(prev => new Map(prev).set(text, false));
                  message.warning(`Global property definition for "${text}" not found`);
                }
              }}
              title="View Global Property Definition"
              style={{ padding: 0, height: 'auto', fontSize: 11 }}
            >
              View Global
            </Button>
          </Space>
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'property_type',
      key: 'property_type',
      width: 120,
      sorter: (a: PrimeArtifactPropertyRow, b: PrimeArtifactPropertyRow) => 
        a.property_type.localeCompare(b.property_type),
      filters: [
        { text: 'String', value: 'string' },
        { text: 'Number', value: 'number' },
        { text: 'Boolean', value: 'boolean' },
        { text: 'Date', value: 'date' },
        { text: 'Currency', value: 'currency' },
        { text: 'Duration', value: 'duration' },
        { text: 'URL', value: 'url' },
        { text: 'UUID', value: 'uuid' },
        { text: 'UUID[]', value: 'uuid[]' },
        { text: 'List', value: 'list' }
      ],
      onFilter: (value: any, record: PrimeArtifactPropertyRow) => record.property_type === value,
      render: (type: string) => getPropertyTypeTag(type)
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      sorter: (a: PrimeArtifactPropertyRow, b: PrimeArtifactPropertyRow) => {
        const aDesc = a.description || '';
        const bDesc = b.description || '';
        return aDesc.localeCompare(bDesc);
      }
    },
    {
      title: 'Input Source',
      dataIndex: 'input_source',
      key: 'input_source',
      width: 140,
      sorter: (a: PrimeArtifactPropertyRow, b: PrimeArtifactPropertyRow) => {
        const aSource = a.input_source || '';
        const bSource = b.input_source || '';
        return aSource.localeCompare(bSource);
      },
      filters: [
        { text: 'User Input', value: 'user_input' },
        { text: 'System Generated', value: 'system_generated' },
        { text: 'Referenced Artifact', value: 'referenced_artifact' }
      ],
      onFilter: (value: any, record: PrimeArtifactPropertyRow) => record.input_source === value,
      render: (source?: string) => {
        if (!source) return <Tag color="default">-</Tag>;
        const colorMap: Record<string, string> = {
          user_input: 'blue',
          system_generated: 'green',
          referenced_artifact: 'purple'
        };
        const labelMap: Record<string, string> = {
          user_input: 'User Input',
          system_generated: 'System',
          referenced_artifact: 'Referenced'
        };
        return <Tag color={colorMap[source] || 'default'}>{labelMap[source] || source}</Tag>;
      }
    },
    {
      title: 'Relationship Type',
      key: 'relationship_type',
      width: 150,
      render: (record: PrimeArtifactPropertyRow) => {
        const relType = getPropertyRelationshipType(record);
        const colorMap: Record<RelationshipType, string> = {
          [RelationshipType.CALCULATION]: 'blue',
          [RelationshipType.RATIO]: 'green',
          [RelationshipType.AGGREGATION]: 'orange',
          [RelationshipType.CONDITION]: 'pink',
          [RelationshipType.SWITCH]: 'purple',
          [RelationshipType.INPUT]: 'cyan',
          [RelationshipType.DEFAULT]: 'lime',
          [RelationshipType.GLOBAL_REF]: 'red',
          [RelationshipType.REFERENCED_ARTIFACT]: 'geekblue',
          [RelationshipType.UNKNOWN]: 'default'
        };
        const labelMap: Record<RelationshipType, string> = {
          [RelationshipType.CALCULATION]: 'Calculation',
          [RelationshipType.RATIO]: 'Ratio',
          [RelationshipType.AGGREGATION]: 'Aggregation',
          [RelationshipType.CONDITION]: 'Condition',
          [RelationshipType.SWITCH]: 'Switch',
          [RelationshipType.INPUT]: 'Input',
          [RelationshipType.DEFAULT]: 'Default',
          [RelationshipType.GLOBAL_REF]: 'Global Var',
          [RelationshipType.REFERENCED_ARTIFACT]: 'Reference',
          [RelationshipType.UNKNOWN]: 'Unknown'
        };
        return <Tag color={colorMap[relType]}>{labelMap[relType]}</Tag>;
      }
    },
    {
      title: 'Dependencies',
      key: 'dependencies',
      width: 100,
      align: 'center' as const,
      render: (record: PrimeArtifactPropertyRow) => {
        const counts = getPropertyDependencyCounts(record.id);
        return <Badge count={counts.dependencies} showZero />;
      }
    },
    {
      title: 'Dependents',
      key: 'dependents',
      width: 100,
      align: 'center' as const,
      render: (record: PrimeArtifactPropertyRow) => {
        const counts = getPropertyDependencyCounts(record.id);
        return <Badge count={counts.dependents} showZero />;
      }
    },
    {
      title: 'Created In States',
      key: 'created_states',
      width: 180,
      render: (record: PrimeArtifactPropertyRow) => {
        const createdStates = record.state_mappings?.filter(m => m.mapping_type === 'created');
        if (!createdStates || createdStates.length === 0) {
          return <Tag color="default">None</Tag>;
        }
        return (
          <Space wrap>
            {createdStates.map(m => (
              <Tag key={m.id} color="green">{m.state_name}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Updated In States',
      key: 'updated_states',
      width: 180,
      render: (record: PrimeArtifactPropertyRow) => {
        const updatedStates = record.state_mappings?.filter(m => m.mapping_type === 'updated');
        if (!updatedStates || updatedStates.length === 0) {
          return <Tag color="default">None</Tag>;
        }
        return (
          <Space wrap>
            {updatedStates.map(m => (
              <Tag key={m.id} color="blue">{m.state_name}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Referenced Artifacts',
      key: 'referenced_artifacts',
      width: 200,
      render: (record: PrimeArtifactPropertyRow) => {
        if (!record.referenced_artifacts || record.referenced_artifacts.length === 0) {
          return <Tag color="default">None</Tag>;
        }
        return (
          <Space wrap>
            {record.referenced_artifacts.map(ref => (
              <Tooltip key={ref.id} title={ref.relationship_type || 'Reference'}>
                <Tag color="purple">
                  <LinkOutlined /> {ref.referenced_artifact_type_name || 'Unknown'}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Option Catalog',
      key: 'option_catalog',
      width: 150,
      render: (record: PrimeArtifactPropertyRow) => {
        if (!record.global_option_catalog_ref) {
          return <span>-</span>;
        }
        return (
          <Tag color="cyan">
            {record.global_option_catalog_name || 'Unknown Catalog'}
          </Tag>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (record: PrimeArtifactProperty) => (
        <Space>
          <Tooltip title="View relationships">
            <Button
              type="link"
              icon={<LinkOutlined />}
              onClick={() => setSelectedPropertyForDetails(record.id)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Edit overrides (simplified)">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleOpenSimplifiedEdit(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Advanced edit (all fields)">
            <Button
              type="link"
              icon={<SettingOutlined />}
              onClick={() => handleEditProperty(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Delete this property?"
            description="This will remove the property definition. Are you sure?"
            onConfirm={() => handleDeleteProperty(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete property">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className={styles.primeArtifactPropertiesManager}>
        {properties.length === 0 && (
          <Alert
            message="No Properties Found"
            description="Properties defined in your artifact type template or states need to be applied to this artifact to appear here. Click 'Apply Properties from Template' to create property records from your template definitions."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Alert
          message="Properties Inherit from Global Definitions"
          description={
            <span>
              All properties must be based on global property definitions. The property type, description, validation rules, and 
              input source are inherited from the global property. You can only override: <strong>is_required</strong>, <strong>default_value</strong>, 
              <strong>property_scope</strong>, and <strong>state mappings</strong> for this specific artifact.
            </span>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

      {/* Compact Completeness Dashboard */}
      <PrimeArtifactCompletenessDashboard
        artifactId={artifactId}
        compact={true}
        onGenerateClick={() => setShowGenerationWizard(true)}
      />

      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          onClick={() => setShowGenerationWizard(true)}
        >
          Generation Wizard
        </Button>
        <Button
          onClick={handleApplyProperties}
          loading={applyLoading}
          type="default"
        >
          Apply Properties from Template
        </Button>
      </Space>

      <PrimeArtifactPropertyTable
          columns={columns}
        dataSource={tableData}
          loading={loading}
        searchText={searchText}
        filterSource={filterSource}
        filterType={filterType}
        onSearchChange={setSearchText}
        onFilterSourceChange={setFilterSource}
        onFilterTypeChange={setFilterType}
        onRefresh={handleRefresh}
        onRowClick={(record) => {
          setSelectedPropertyForDetails(record.id);
        }}
        onAddProperty={handleAddProperty}
        onEditProperty={handleEditProperty}
        onDeleteProperty={(property) => handleDeleteProperty(property.id)}
        selectedPropertyId={selectedPropertyForDetails}
        artifactName={artifactName || ''}
        totalProperties={properties.length}
      />

      <Modal
        title={editingProperty ? 'Edit Property' : 'Add Property'}
        open={showModal}
        onOk={handleSaveProperty}
        onCancel={() => setShowModal(false)}
        width={800}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            label="Property Key"
            name="property_key"
            rules={[{ required: true, message: 'Please enter property key' }]}
          >
            <Input placeholder="e.g., has_business_plan" disabled={!!editingProperty} />
          </Form.Item>

          <Form.Item
            label="Property Type"
            name="property_type"
            rules={[{ required: true, message: 'Please select property type' }]}
          >
            <Select placeholder="Select property type">
              <Option value="string">String</Option>
              <Option value="number">Number</Option>
              <Option value="boolean">Boolean</Option>
              <Option value="date">Date</Option>
              <Option value="date_range">Date Range</Option>
              <Option value="currency">Currency</Option>
              <Option value="duration">Duration</Option>
              <Option value="url">URL</Option>
              <Option value="uuid">UUID</Option>
              <Option value="uuid[]">Array of UUIDs</Option>
              <Option value="list">List (from Option Catalog)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Input Source"
            name="input_source"
            tooltip="How this property value is obtained"
          >
            <Select placeholder="Select input source">
              <Option value="user_input">User Input</Option>
              <Option value="system_generated">System Generated</Option>
              <Option value="referenced_artifact">Referenced Artifact (UUID)</Option>
              <Option value="transformed_reference">Transformed Reference (Calculated)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Property Scope"
            name="property_scope"
            tooltip="Determines whether this property applies to the artifact level or artifactItem level. Artifact scope: applies to the artifact itself (e.g., reporting_period_type). ArtifactItem scope: applies to individual artifactItems (e.g., reporting_period_daterange)."
          >
            <Select placeholder="Select scope (or leave blank to auto-detect from state mappings)">
              <Option value="artifact">Artifact</Option>
              <Option value="artifact_item">Artifact Item</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.input_source !== currentValues.input_source
            }
          >
            {({ getFieldValue }) => {
              const inputSource = getFieldValue('input_source');
              const isReference = inputSource === 'referenced_artifact' || inputSource === 'transformed_reference';
              
              if (!isReference) {
                return null;
              }
              
              return (
                <Form.Item
                  label="Child Reference"
                  name="is_child_reference"
                  valuePropName="checked"
                  tooltip="Child references require creation (have 'created' state mappings) and the workflow waits for the child artifact to be created and returned. Reference-only properties are read-only (only 'updated' state mappings) and don't impact the referenced artifact's workflow."
                >
                  <Checkbox>This is a child reference (requires creation step)</Checkbox>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.input_source !== currentValues.input_source ||
              prevValues.property_type !== currentValues.property_type
            }
          >
            {({ getFieldValue }) => {
              const inputSource = getFieldValue('input_source');
              const propertyType = getFieldValue('property_type');
              
              if (inputSource !== 'user_input') {
                return null;
              }
              
              return (
                <>
                  <Form.Item
                    label="Input Label"
                    name="input_label"
                    tooltip="User-facing label for this input field. If not specified, property_key will be used."
                  >
                    <Input placeholder="Label for user input (e.g., 'Reporting Period Type')" />
                  </Form.Item>

                  <Form.Item
                    label="Input Type"
                    name="input_type"
                    tooltip="Specific input type for formatting. Usually matches property type, but can be overridden for lists."
                  >
                    <Select placeholder="Select input type">
                      <Option value="text">Text</Option>
                      <Option value="number">Number</Option>
                      <Option value="boolean">Boolean (Checkbox)</Option>
                      <Option value="date">Date</Option>
                      <Option value="date_range">Date Range</Option>
                      <Option value="currency">Currency</Option>
                      <Option value="duration">Duration</Option>
                      <Option value="url">URL</Option>
                      <Option value="select">Select (Dropdown)</Option>
                      <Option value="multi_select">Multi-Select</Option>
                      <Option value="textarea">Textarea</Option>
                    </Select>
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <TextArea rows={2} placeholder="Describe this property" />
          </Form.Item>

          <Form.Item
            label="Required"
            name="is_required"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label="Global Option Catalog"
            name="global_option_catalog_ref"
            tooltip="Required for 'List' type. Select or create a catalog for list options"
            dependencies={['property_type']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (getFieldValue('property_type') === 'list' && !value) {
                    return Promise.reject(new Error('Option catalog is required for List type'));
                  }
                  return Promise.resolve();
                }
              })
            ]}
          >
            <Select
              placeholder={optionCatalogs.length === 0 ? 'No catalogs available. Click "Manage Catalogs" to create one.' : 'Select option catalog'}
              allowClear
              showSearch
              loading={optionCatalogs.length === 0}
              filterOption={(input, option) => {
                const label = typeof option?.label === 'string' 
                  ? option.label 
                  : typeof option?.children === 'string' 
                    ? option.children 
                    : String(option?.label || option?.children || '');
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              notFoundContent={optionCatalogs.length === 0 ? 'No catalogs available. Click "Manage Catalogs" to create one.' : 'No catalogs found'}
              onOpenChange={(open) => {
                if (open) {
                  // Refresh catalogs when dropdown opens
                  console.log('[PrimeArtifactPropertiesManager] Option catalog dropdown opened, refreshing catalogs...');
                  fetchOptionCatalogs();
                }
              }}
              popupRender={(menu) => (
                <>
                  {menu}
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setShowCatalogManager(true);
                    }}
                    style={{ width: '100%', textAlign: 'center' }}
                  >
                    Manage Catalogs
                  </Button>
                </>
              )}
            >
              {optionCatalogs.length > 0 ? (
                optionCatalogs.map(catalog => (
                  <Option key={catalog.id} value={catalog.id}>
                    {catalog.name}
                  </Option>
                ))
              ) : (
                <Option disabled value="no-catalogs">
                  No catalogs available
                </Option>
              )}
            </Select>
          </Form.Item>

          <Form.Item
            label="Default Value"
            name="default_value"
          >
            <Input placeholder="Default value for this property" />
          </Form.Item>

          {/* Conditional fields for item_generation property */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.property_key !== currentValues.property_key ||
              prevValues.property_scope !== currentValues.property_scope ||
              prevValues.default_value !== currentValues.default_value
            }
          >
            {({ getFieldValue }) => {
              const propertyKey = getFieldValue('property_key');
              const propertyScope = getFieldValue('property_scope');
              const itemGenerationValue = getFieldValue('default_value');
              
              // Show conditional fields if this is the item_generation property with artifact scope
              if (propertyKey !== 'item_generation' || propertyScope !== 'artifact') {
                return null;
              }
              
              // If item_generation is set to 'reporting_period', show additional fields
              const showReportingPeriodFields = itemGenerationValue === 'reporting_period';
              
              return (
                <>
                  {showReportingPeriodFields && (
                    <>
                      <Form.Item
                        label="Reporting Period Type"
                        name="reporting_period_type"
                        tooltip="Type of reporting period (weekly, monthly, quarterly, etc.)"
                      >
                        <Select
                          placeholder="Select reporting period type"
                          allowClear
                          showSearch
                          filterOption={(input, option) => {
                            const label = typeof option?.label === 'string' 
                              ? option.label 
                              : typeof option?.children === 'string' 
                                ? option.children 
                                : String(option?.label || option?.children || '');
                            return label.toLowerCase().includes(input.toLowerCase());
                          }}
                        >
                          {(() => {
                            const reportingPeriodCatalog = optionCatalogs.find(catalog => catalog.name === 'reporting_period_types');
                            if (!reportingPeriodCatalog || !reportingPeriodCatalog.values || reportingPeriodCatalog.values.length === 0) {
                              return (
                                <Option disabled value="no-values">
                                  No reporting period types available
                                </Option>
                              );
                            }
                            return reportingPeriodCatalog.values.map(value => (
                              <Option key={value.value} value={value.value}>
                                {value.display_label || value.value}
                              </Option>
                            ));
                          })()}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label="Fiscal Year Start"
                        name="fiscal_year_start"
                        tooltip="Start date of the fiscal year in MM-DD format (e.g., '01-01' for January 1st)"
                      >
                        <Input 
                          placeholder="MM-DD (e.g., 01-01)" 
                          pattern="^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$"
                        />
                      </Form.Item>

                      <Form.Item
                        label="Reporting Period Start Date"
                        name="reporting_period_start_date"
                        tooltip="Start date for reporting period generation"
                      >
                        <Input type="date" />
                      </Form.Item>

                      <Form.Item
                        label="Reporting Period End Date (Optional)"
                        name="reporting_period_end_date"
                        tooltip="End date for reporting period generation (leave blank for open-ended)"
                      >
                        <Input type="date" />
                      </Form.Item>
                    </>
                  )}
                </>
              );
            }}
          </Form.Item>

          <Form.Item
            label="Created In States"
            name="created_in_states"
            tooltip="Select states where this property is created (initialized for the first time)"
          >
            <Select
              mode="multiple"
              placeholder="Select states where property is created"
              allowClear
            >
              {artifactStates.map(state => (
                <Option key={state.id} value={state.name}>
                  {state.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Updated In States"
            name="updated_in_states"
            tooltip="Select states where this property value is updated"
          >
            <Select
              mode="multiple"
              placeholder="Select states where property is updated"
              allowClear
            >
              {artifactStates.map(state => (
                <Option key={state.id} value={state.name}>
                  {state.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.input_source !== currentValues.input_source ||
              prevValues.property_type !== currentValues.property_type
            }
          >
            {({ getFieldValue }) => {
              const inputSource = getFieldValue('input_source');
              const propertyType = getFieldValue('property_type');
              // Show artifact refs for UUID types OR for referenced_artifact input source with any type
              const showArtifactRefs = inputSource === 'referenced_artifact';
              
              if (!showArtifactRefs) {
                return null;
              }
              
              // Determine if this is a UUID reference (direct UUID) or property copy (non-UUID)
              // This is recalculated inside the render function so it updates when property_type changes
              const isUuidReference = propertyType === 'uuid' || propertyType === 'uuid[]';

              return (
                <Form.Item
                  label={isUuidReference ? "Referenced Artifacts" : "Source Artifact & Property"}
                  name="referenced_artifact_types"
                  tooltip={
                    isUuidReference 
                      ? "Select specific artifact instances that this property references. You can search by name or filter by type."
                      : "Select an artifact and property to copy the value from (e.g., currency, reporting period dates). Use 'Copy Value' transformation for simple property copying."
                  }
                  rules={[
                    {
                      validator: (_, value) => {
                        // Only validate if input_source is referenced_artifact
                        const inputSource = form.getFieldValue('input_source');
                        if (inputSource === 'referenced_artifact') {
                          if (!value || value.length === 0) {
                            return Promise.reject(new Error('At least one artifact must be selected for referenced artifact properties'));
                          }
                          // Validate that each reference has an artifact_id
                          const invalidRefs = value.filter((ref: any) => !ref || !ref.artifact_id);
                          if (invalidRefs.length > 0) {
                            return Promise.reject(new Error('All artifact references must have a selected artifact'));
                          }
                          // For non-UUID types, require source_property_key
                          if (!isUuidReference) {
                            const invalidPropertyRefs = value.filter((ref: any) => !ref || !ref.source_property_key);
                            if (invalidPropertyRefs.length > 0) {
                              return Promise.reject(new Error('For non-UUID properties, you must select a source property to copy from'));
                            }
                          }
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Form.List name="referenced_artifact_types">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field, index) => (
                          <Card key={`referenced-artifact-list-${field.name}-${field.key || index}`} size="small" style={{ marginBottom: 16 }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                              <Space wrap>
                                <Form.Item
                                  name={[field.name, 'artifact_type_id']}
                                  label="Artifact Type (Optional)"
                                  tooltip="Filter by artifact type, or leave blank to search all artifacts"
                                  style={{ width: 250 }}
                                >
                                  <Select
                                    placeholder="Filter by type (optional)"
                                    showSearch
                                    allowClear
                                    filterOption={(input, option) =>
                                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={(value) => {
                                      setSelectedSourceArtifactType(value);
                                      form.setFieldValue([field.name, 'artifact_id'], undefined);
                                      form.setFieldValue([field.name, 'source_property_key'], undefined);
                                      fetchSourceArtifacts(value);
                                    }}
                                  >
                                    {artifactTypes.map(type => (
                                      <Option key={type.id} value={type.id}>
                                        {type.name}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                                
                                <Form.Item
                                  name={[field.name, 'artifact_id']}
                                  label="Artifact"
                                  rules={[{ required: true, message: 'Select or create artifact' }]}
                                  style={{ width: 300 }}
                                >
                                  <Select
                                    placeholder="Search by name or select artifact"
                                    showSearch
                                    loading={sourceArtifactsLoading}
                                    filterOption={false}
                                    onSearch={(search) => {
                                      const selectedType = form.getFieldValue(['referenced_artifact_types', field.name, 'artifact_type_id']);
                                      fetchSourceArtifacts(selectedType, search);
                                    }}
                                    onFocus={() => {
                                      const selectedType = form.getFieldValue(['referenced_artifact_types', field.name, 'artifact_type_id']);
                                      fetchSourceArtifacts(selectedType);
                                    }}
                                    onChange={async (value) => {
                                      // Update artifact_type_id if not set, based on selected artifact
                                      const selectedArtifact = sourceArtifacts.find((a: any) => a.id === value);
                                      if (selectedArtifact && !form.getFieldValue([field.name, 'artifact_type_id'])) {
                                        form.setFieldValue([field.name, 'artifact_type_id'], selectedArtifact.artifact_type_id);
                                        setSelectedSourceArtifactType(selectedArtifact.artifact_type_id);
                                      }
                                      // Always fetch properties when artifact is selected (for non-UUID types)
                                      // Check current property_type to determine if it's UUID
                                      const currentPropertyType = form.getFieldValue('property_type');
                                      const isUuidType = currentPropertyType === 'uuid' || currentPropertyType === 'uuid[]';
                                      if (!isUuidType && value) {
                                        await fetchSourceArtifactProperties(value);
                                        // Update hidden field to trigger re-render when properties are loaded
                                        // Use timestamp to ensure it changes
                                        form.setFieldValue(['_properties_loaded', field.name], Date.now());
                                      }
                                    }}
                                    notFoundContent={
                                      <Button
                                        type="link"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                          const selectedType = form.getFieldValue(['referenced_artifact_types', field.name, 'artifact_type_id']);
                                          artifactCreateForm.setFieldsValue({ artifact_type_id: selectedType });
                                          setShowArtifactCreateModal(true);
                                        }}
                                      >
                                        Create new artifact
                                      </Button>
                                    }
                                  >
                                    {sourceArtifacts.map((artifact: any) => (
                                      <Option key={artifact.id} value={artifact.id}>
                                        {artifact.name}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                                
                                {!isUuidReference && (
                                  <>
                                    {/* Hidden field to trigger re-render when properties are loaded */}
                                    <Form.Item
                                      noStyle
                                      name={['_properties_loaded', field.name]}
                                      initialValue={0}
                                    >
                                      <Input type="hidden" />
                                    </Form.Item>
                                    <Form.Item
                                      noStyle
                                      shouldUpdate={(prevValues, currentValues) => {
                                        const prevArtifactId = prevValues?.referenced_artifact_types?.[field.name]?.artifact_id;
                                        const currArtifactId = currentValues?.referenced_artifact_types?.[field.name]?.artifact_id;
                                        // Also trigger when property_type changes (affects isUuidReference)
                                        const prevPropertyType = prevValues?.property_type;
                                        const currPropertyType = currentValues?.property_type;
                                        // Also trigger when properties might have been loaded
                                        const prevPropsLoaded = prevValues?._properties_loaded?.[field.name];
                                        const currPropsLoaded = currentValues?._properties_loaded?.[field.name];
                                        return prevArtifactId !== currArtifactId || prevPropertyType !== currPropertyType || prevPropsLoaded !== currPropsLoaded;
                                      }}
                                    >
                                    {() => {
                                      const artifactId = form.getFieldValue(['referenced_artifact_types', field.name, 'artifact_id']);
                                      const currentPropertyKey = form.getFieldValue('property_key');
                                      const isDisabled = !artifactId;
                                      
                                      // Get properties from the map (fetched when artifact is selected)
                                      // Check if properties have been fetched (even if empty array)
                                      const propertiesHaveBeenFetched = artifactId && artifactId in sourceArtifactPropertiesMap;
                                      const propertiesRaw = propertiesHaveBeenFetched 
                                        ? sourceArtifactPropertiesMap[artifactId]
                                        : undefined;
                                      // Ensure properties is always an array
                                      const properties = Array.isArray(propertiesRaw) ? propertiesRaw : [];
                                      const hasProperties = properties.length > 0;
                                      const isLoading = artifactId ? (sourcePropertiesLoading[artifactId] || false) : false;
                                      
                                      // Field should be enabled if artifact is selected, even if properties haven't been fetched yet or are empty
                                      // The field is only disabled if no artifact is selected
                                      
                                      // Debug logging
                                      if (artifactId && !isLoading && propertiesHaveBeenFetched && !hasProperties) {
                                        console.log(`Artifact ${artifactId} has no properties (empty array). Field should be enabled for creating properties.`);
                                      } else if (artifactId && !isLoading && !propertiesHaveBeenFetched) {
                                        console.log(`Properties not yet fetched for artifact ${artifactId}. Fetching now...`);
                                        // Trigger fetch if not already loading
                                        if (!isLoading) {
                                          fetchSourceArtifactProperties(artifactId).then(() => {
                                            form.setFieldValue(['_properties_loaded', field.name], Date.now());
                                          });
                                        }
                                      }
                                      
                                      // Check if current property key exists in source artifact properties
                                      const propertyExists = currentPropertyKey && artifactId && Array.isArray(properties) && properties.some(
                                        (p: any) => p.property_key === currentPropertyKey
                                      );
                                      // Show create prompt if:
                                      // 1. Not loading, not disabled, has artifactId and propertyKey
                                      // 2. Property doesn't exist
                                      // 3. Either has properties OR no properties (so user can create first property)
                                      const shouldShowCreatePrompt = !isLoading && !isDisabled && currentPropertyKey && artifactId && !propertyExists && propertiesHaveBeenFetched;
                                      
                                      return (
                                        <>
                                          <Form.Item
                                            key={`source-property-ref-${field.name}-${artifactId || 'none'}-${propertiesUpdateKey}-${hasProperties}`}
                                            name={[field.name, 'source_property_key']}
                                            label="Source Property"
                                            rules={[{ required: true, message: 'Select source property to copy from' }]}
                                            style={{ width: 200 }}
                                          >
                                            {isDisabled ? (
                                              <Select
                                                placeholder="Select source artifact first"
                                                disabled={true}
                                                notFoundContent={
                                                  <div style={{ padding: '8px', textAlign: 'center', color: '#999' }}>
                                                    Select a source artifact above to enable property selection
                                                  </div>
                                                }
                                              />
                                            ) : isLoading ? (
                                              <Select
                                                placeholder="Loading properties..."
                                                loading={true}
                                                disabled={true}
                                              />
                                            ) : !propertiesHaveBeenFetched ? (
                                              <Select
                                                placeholder="Loading properties..."
                                                disabled={true}
                                                loading={true}
                                              />
                                            ) : !hasProperties ? (
                                              <Input
                                                placeholder="Enter property key (create property on source artifact first)"
                                                allowClear
                                                onChange={(e) => {
                                                  const value = e.target.value;
                                                  if (value) {
                                                    // Set transformation logic to copy_value
                                                    form.setFieldValue([field.name, 'transformation_logic'], {
                                                      type: 'copy_value',
                                                      source_property: value
                                                    });
                                                    form.setFieldValue([field.name, 'reference_type'], 'transformed');
                                                  }
                                                }}
                                              />
                                            ) : (
                                              <Select
                                                placeholder="Select property"
                                                showSearch
                                                filterOption={(input, option) =>
                                                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                loading={isLoading}
                                                onChange={(value) => {
                                                  // Set transformation logic to copy_value
                                                  form.setFieldValue([field.name, 'transformation_logic'], {
                                                    type: 'copy_value',
                                                    source_property: value
                                                  });
                                                  form.setFieldValue([field.name, 'reference_type'], 'transformed');
                                                }}
                                              >
                                                {properties.map((prop: any) => (
                                                  <Option key={prop.property_key} value={prop.property_key}>
                                                    {prop.property_key} ({prop.property_type})
                                                  </Option>
                                                ))}
                                              </Select>
                                            )}
                                          </Form.Item>
                                          
                                          {shouldShowCreatePrompt && (
                                            <Alert
                                              message="Property Not Found on Source Artifact"
                                              description={
                                                <Space direction="vertical" size="small">
                                                  <Text>
                                                    Property <strong>"{currentPropertyKey}"</strong> does not exist on the source artifact.
                                                  </Text>
                                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                                    For traceability, the property should also exist on the source artifact. Create it now?
                                                  </Text>
                                                  <Button
                                                    type="primary"
                                                    size="small"
                                                    icon={<PlusOutlined />}
                                                    onClick={() => handleCreatePropertyOnSourceArtifact(artifactId, currentPropertyKey, field.name)}
                                                  >
                                                    Create "{currentPropertyKey}" on Source Artifact
                                                  </Button>
                                                </Space>
                                              }
                                              type="warning"
                                              showIcon
                                              style={{ marginTop: 8 }}
                                              closable
                                            />
                                          )}
                                        </>
                                      );
                                    }}
                                  </Form.Item>
                                  </>
                                )}
                                
                                <Form.Item
                                  name={[field.name, 'relationship_type']}
                                  label="Relationship"
                                  style={{ width: 150 }}
                                >
                                  <Select placeholder="Relationship" allowClear>
                                    <Option value="references">References</Option>
                                    <Option value="parent">Parent</Option>
                                    <Option value="child">Child</Option>
                                    <Option value="material">Material</Option>
                                    <Option value="resource">Resource</Option>
                                  </Select>
                                </Form.Item>
                                
                                <Button
                                  type="link"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => remove(field.name)}
                                  style={{ marginTop: 30 }}
                                >
                                  Remove
                                </Button>
                              </Space>
                              
                              <Form.Item
                                name={[field.name, 'notes']}
                                label="Notes"
                              >
                                <Input placeholder="Notes (optional)" />
                              </Form.Item>
                            </Space>
                          </Card>
                        ))}
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                        >
                          Add Artifact Type Reference
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Form.Item>
              );
            }}
          </Form.Item>

          {/* Transformed Reference Section */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.input_source !== currentValues.input_source
            }
          >
            {({ getFieldValue }) => {
              const inputSource = getFieldValue('input_source');
              const showTransformedRef = inputSource === 'transformed_reference';
              
              if (!showTransformedRef) {
                return null;
              }

              return (
                <Form.Item
                  label="Calculation Configuration"
                  tooltip="Configure how this property is calculated from other properties. Can use properties from the same artifact or external artifacts."
                >
                  <Form.List name="transformed_references">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field, index) => (
                          <Card key={`transformed-ref-list-${field.name}-${field.key || index}`} size="small" style={{ marginBottom: 16 }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                              <CalculationBuilder
                                form={form}
                                fieldName={field.name}
                                artifactId={artifactId}
                                currentProperties={properties.filter((p: PrimeArtifactProperty) => p.property_key !== form.getFieldValue('property_key')).map((p: PrimeArtifactProperty) => ({
                                  property_key: p.property_key,
                                  property_type: p.property_type
                                }))}
                                onCalculationTypeChange={(type) => {
                                  // Optional: handle calculation type change
                                }}
                              />
                              
                              <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(field.name)}
                                style={{ marginTop: 8 }}
                              >
                                Remove Calculation
                              </Button>
                            </Space>
                          </Card>
                        ))}
                        <Button
                          type="dashed"
                          onClick={() => add({ reference_type: 'transformed' })}
                          block
                          icon={<PlusOutlined />}
                        >
                          Add Calculation
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            label="Validation Rules (JSON)"
            name="validation_rules"
            tooltip="JSON object with validation rules (e.g., min, max, pattern, etc.)"
          >
            <TextArea
              rows={4}
              placeholder='{"min": 0, "max": 100}'
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Manage Option Catalogs"
        open={showCatalogManager}
        onCancel={() => setShowCatalogManager(false)}
        footer={null}
        width={1000}
      >
        <OptionCatalogManager
          visible={showCatalogManager}
          onClose={() => {
            setShowCatalogManager(false);
            // Refresh catalogs when catalog manager closes
            console.log('[PrimeArtifactPropertiesManager] Catalog manager closed, refreshing catalogs...');
            fetchOptionCatalogs();
          }}
          onCatalogSelected={(catalogId) => {
            console.log('[PrimeArtifactPropertiesManager] Catalog selected:', catalogId);
            // Refresh catalogs first, then set the value
            fetchOptionCatalogs().then(() => {
              form.setFieldsValue({ global_option_catalog_ref: catalogId });
              setShowCatalogManager(false);
            });
          }}
        />
      </Modal>

      {/* Create Artifact Modal */}
      <Modal
        title="Create New Artifact"
        open={showArtifactCreateModal}
        onOk={async () => {
          try {
            const newArtifactId = await handleCreateArtifact();
            // If we're in a transformed reference form, set the new artifact
            const transformedRefs = form.getFieldValue('transformed_references');
            const uuidRefs = form.getFieldValue('referenced_artifact_types');
            
            // Find the last reference field and set the artifact_id
            if (transformedRefs && transformedRefs.length > 0) {
              const lastIndex = transformedRefs.length - 1;
              form.setFieldValue(['transformed_references', lastIndex, 'artifact_id'], newArtifactId);
              // Fetch properties for the new artifact
              await fetchSourceArtifactProperties(newArtifactId);
            } else if (uuidRefs && uuidRefs.length > 0) {
              const lastIndex = uuidRefs.length - 1;
              // Get artifact details to add to sourceArtifacts list
              try {
                const artifactResponse = await apiClient.get(`/prime-artifacts/${newArtifactId}`);
                const newArtifact = artifactResponse.data;
                // Add new artifact to sourceArtifacts list
                setSourceArtifacts(prev => {
                  const exists = prev.some((a: any) => a.id === newArtifactId);
                  if (!exists && newArtifact) {
                    return [...prev, {
                      id: newArtifact.id,
                      name: newArtifact.name,
                      artifact_type_id: newArtifact.artifact_type_id
                    }];
                  }
                  return prev;
                });
              } catch (error) {
                console.warn('Could not fetch new artifact details:', error);
              }
              
              form.setFieldValue(['referenced_artifact_types', lastIndex, 'artifact_id'], newArtifactId);
              
              // For non-UUID references, fetch properties for the new artifact (even if empty, it enables the field)
              const currentPropertyType = form.getFieldValue('property_type');
              const isUuidType = currentPropertyType === 'uuid' || currentPropertyType === 'uuid[]';
              if (!isUuidType) {
                // Fetch properties (will be empty for new artifact, but enables the field)
                await fetchSourceArtifactProperties(newArtifactId);
                // Update hidden field to trigger re-render when properties are loaded
                form.setFieldValue(['_properties_loaded', lastIndex], Date.now());
              }
              
              // Refresh artifacts list (after setting form values)
              const selectedType = form.getFieldValue(['referenced_artifact_types', lastIndex, 'artifact_type_id']);
              await fetchSourceArtifacts(selectedType);
            }
          } catch (error) {
            // Error already shown in handleCreateArtifact
          }
        }}
        onCancel={() => {
          setShowArtifactCreateModal(false);
          artifactCreateForm.resetFields();
        }}
        okText="Create"
        cancelText="Cancel"
      >
        <Form form={artifactCreateForm} layout="vertical">
          <Form.Item
            name="artifact_type_id"
            label="Artifact Type"
            rules={[{ required: true, message: 'Artifact type is required' }]}
          >
            <Select
              placeholder="Select artifact type"
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {artifactTypes.map(type => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="name"
            label="Artifact Name"
            rules={[{ required: true, message: 'Artifact name is required' }]}
          >
            <Input placeholder="Enter artifact name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Enter description (optional)" />
          </Form.Item>
          <Form.Item
            name="parent_ref"
            label="Parent Artifact (Optional)"
            tooltip="Select a parent artifact to create this as a child artifact. Leave empty to create a root artifact."
          >
            <Select
              placeholder="Select parent artifact (optional)"
              allowClear
              showSearch
              loading={loadingAllArtifacts}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
              }
              options={allPrimeArtifacts.map(artifact => ({
                value: artifact.id,
                label: artifact.name
              }))}
            />
          </Form.Item>
          <Form.Item
            name="state_template_id"
            label="State Template (Optional)"
            tooltip="Select a state template to apply to this artifact. If not selected, the artifact type template will be used."
          >
            <Select
              placeholder="Select state template (optional)"
              allowClear
              showSearch
              loading={loadingStateTemplates}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
              }
              options={stateTemplates.map(template => ({
                value: template.id,
                label: `${template.name}${template.version ? ` (v${template.version})` : ''}`
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
      {showGlobalPropertyModal && selectedGlobalPropertyKey && (
        <GlobalPropertyDetailsModal
          propertyKey={selectedGlobalPropertyKey as string}
          visible={showGlobalPropertyModal}
          onClose={() => {
            setShowGlobalPropertyModal(false);
            setSelectedGlobalPropertyKey(null);
          }}
        />
      )}
      <GlobalPropertySelector
        visible={showGlobalPropertySelector}
        artifactId={artifactId}
        onCancel={() => setShowGlobalPropertySelector(false)}
        onSelect={handlePropertySelect}
        excludeProperties={properties.map(p => p.property_key)}
        context="artifact"
      />
      {showRelationshipGraph && (
        <Modal
          title={`Property Relationships: ${artifactName}`}
          open={showRelationshipGraph}
          onCancel={() => setShowRelationshipGraph(false)}
          footer={null}
          width="90%"
          style={{ top: 20 }}
        >
          <PropertyRelationshipGraph
            artifactId={artifactId}
            artifactName={artifactName}
            onPropertySelect={(propertyId) => {
              setSelectedPropertyForDetails(propertyId);
              setShowRelationshipGraph(false);
            }}
          />
        </Modal>
      )}

      <PropertyRelationshipDetails
        artifactId={artifactId}
        propertyId={selectedPropertyForDetails}
        visible={selectedPropertyForDetails !== null}
        onClose={() => setSelectedPropertyForDetails(null)}
      />


      {/* Simplified Property Form for editing overrides */}
      <SimplifiedPropertyForm
        visible={showSimplifiedPropertyForm}
        propertyKey={simplifiedFormPropertyKey}
        artifactId={artifactId}
        editingPropertyId={simplifiedFormPropertyId}
        existingOverrides={simplifiedFormOverrides}
        onCancel={handleSimplifiedFormClose}
        onSave={handleSimplifiedFormSave}
      />

      {/* Generation Wizard for complete artifact setup */}
      <PrimeArtifactGenerationWizard
        visible={showGenerationWizard}
        artifactId={artifactId}
        artifactName={artifactName || 'Artifact'}
        onClose={() => setShowGenerationWizard(false)}
        onComplete={() => {
          fetchProperties();
          onPropertiesUpdated?.();
        }}
      />
    </div>
  );
};

export default PrimeArtifactPropertiesManager;

